<template>
  <div class="settings">
    <F2gDialogLoader :value="settings.status === 'loading'" />
    <div class="settings__wrapper">
      <v-flex sm12 xs12>
        <F2gCard padding class="pb-4">
          <!-- language -->
          <F2gSubtitle :marginTop="false">{{
            $t("common.language")
          }}</F2gSubtitle>
          <v-layout>
            <v-flex sm2 xs12>
              <v-select
                :label="$t('common.language')"
                :items="settings.languages"
                :item-text="$PageUtils.getNameLower()"
                item-value="code"
                v-model="language"
                @change="changeLanguage"
              ></v-select>
            </v-flex>
          </v-layout>
          <!-- notifications -->
          <F2gSubtitle>{{ $t("mobile.settings.moreSettings") }}</F2gSubtitle>
          <v-layout class="px-2">
            <v-flex sm2 xs12>
              <v-select
                :label="$t('common.notifications')"
                :items="notificationTypes"
                item-text="name"
                return-object
                v-model="notificationType"
                @change="updateNotificationType"
              ></v-select>
            </v-flex>
          </v-layout>
          <v-layout class="px-2">
            <v-flex sm2 xs12>
              <v-select
                :label="$t('helpTexts')"
                :items="helpTextsOptions"
                item-text="name"
                return-object
                v-model="helpTextSelectedOption"
                @change="updateHelpTextsDetailVisibility"
              ></v-select>
            </v-flex>
          </v-layout>
          <!-- number results -->
          <F2gSubtitle>{{ $t("common.tablesNumberResults") }}</F2gSubtitle>
          <v-layout>
            <v-flex sm2 xs12>
              <v-select
                :label="$t('common.resultsNumber')"
                :items="results"
                v-model="result"
              ></v-select>
            </v-flex>
          </v-layout>
          <!-- chat position -->
          <F2gSubtitle v-if="isProduction">{{
            $t("chatPosition")
          }}</F2gSubtitle>
          <v-layout v-if="isProduction">
            <v-flex sm2 xs12>
              <v-select
                :label="$t('chatPosition')"
                :items="chatPositions"
                item-text="name"
                v-model="chatPosition"
                return-object
                @change="updateChatPosition"
                ><template
                  v-if="$PageUtils.canShowHelpTexts()"
                  slot="append-outer"
                  ><F2gIcon
                    class="help-icon"
                    value="question-mark"
                    :height="17"
                    :width="17"
                    @click="showChatPositionHelp = !showChatPositionHelp"
                  /> </template
              ></v-select>

              <transition name="fade">
                <F2gUxInfo
                  v-if="showChatPositionHelp"
                  :message="$t('chatPositionHelpText')"
                  :showHelpIcon="false"
                  :isForm="false"
                  :showHelpMessageFromOutside="showChatPositionHelp"
                  :helpCardTopPixels="-16"
                  :helpCardLeftPixels="-10"
                  :helpCardWidth="300"
                  @clickedAway="showChatPositionHelp = false"
                />
              </transition>
            </v-flex>
          </v-layout>
          <!-- timezone -->
          <v-layout align-center mb-2>
            <v-flex sm2 xs12>
              <span>{{ $t("common.showTimeZoneTooltips") }}</span>
            </v-flex>
            <v-flex sm2 xs12>
              <v-switch
                class="ml-2"
                style="width: 65px"
                :color="ownerColors.ownerPrimaryColor"
                v-model="showDateTooltip"
                ><template v-if="$PageUtils.canShowHelpTexts()" slot="append">
                  <F2gIcon
                    class="help-icon mt-1"
                    value="question-mark"
                    :height="17"
                    :width="17"
                    @click="showTimeZoneHelp = !showTimeZoneHelp"
                  /> </template
              ></v-switch>
              <transition name="fade">
                <F2gUxInfo
                  v-if="showTimeZoneHelp"
                  :message="$t('timeZoneSwitchHelpText')"
                  :showHelpIcon="false"
                  :isForm="false"
                  :showHelpMessageFromOutside="showTimeZoneHelp"
                  :helpCardTopPixels="-64"
                  :helpCardLeftPixels="76"
                  :helpCardWidth="300"
                  :helpCardMinWidthpx="300"
                  @clickedAway="showTimeZoneHelp = false"
                />
              </transition>
            </v-flex>
          </v-layout>
          <div v-if="!blockedPersonalization">
            <!-- colors topbar -->
            <v-layout align-center mb-2>
              <v-flex sm2 xs12 class="mt-2 mr-2">
                <span>{{ $t("common.usePersonalizedColorsInTopbar") }}</span>
              </v-flex>
              <v-flex sm2 xs12>
                <v-switch
                  class="ml-2"
                  style="width: 65px"
                  :color="ownerColors.ownerPrimaryColor"
                  v-model="showPersonalizationColorsTopbar"
                  @change="setPersonalizationColorsTopbar"
                >
                  <template v-if="$PageUtils.canShowHelpTexts()" slot="append">
                    <F2gIcon
                      v-if="checkHelpTextSettings"
                      class="help-icon mt-1"
                      value="question-mark"
                      :height="17"
                      :width="17"
                      @click="
                        showPersonalizationTopbarHelp =
                          !showPersonalizationTopbarHelp
                      "
                    />
                  </template>
                </v-switch>
                <transition name="fade">
                  <F2gUxInfo
                    v-if="showPersonalizationTopbarHelp"
                    :message="$t('useTopbarMenusColorHelpText')"
                    :showHelpIcon="false"
                    :isForm="false"
                    :showHelpMessageFromOutside="showPersonalizationTopbarHelp"
                    :helpCardTopPixels="-64"
                    :helpCardLeftPixels="76"
                    :helpCardWidth="300"
                    :helpCardMinWidthpx="300"
                    @clickedAway="showPersonalizationTopbarHelp = false"
                  />
                </transition>
              </v-flex>
            </v-layout>
            <!-- colors content -->
            <v-layout align-center mb-2>
              <v-flex sm2 xs12 class="mt-4 mr-2">
                <span>{{ $t("common.useColorPersonalizationContent") }}</span>
              </v-flex>
              <v-layout sm3 xs12>
                <span>
                  <v-switch
                    class="mt-3"
                    :color="ownerColors.ownerPrimaryColor"
                    v-model="showPersonalizationColorsContent"
                    @change="setPersonalizationColorsContent"
                  >
                    <template
                      v-if="$PageUtils.canShowHelpTexts()"
                      slot="append"
                    >
                      <F2gIcon
                        v-if="checkHelpTextSettings"
                        class="help-icon mt-1"
                        value="question-mark"
                        :height="17"
                        :width="17"
                        @click="
                          showPersonalizationContent =
                            !showPersonalizationContent
                        "
                      />
                    </template>
                  </v-switch>
                </span>
                <transition name="fade">
                  <F2gUxInfo
                    v-if="showPersonalizationContent"
                    :message="$t('useBackOfficeColorsHelpText')"
                    :showHelpIcon="false"
                    :isForm="false"
                    :showHelpMessageFromOutside="showPersonalizationContent"
                    :helpCardTopPixels="-20"
                    :helpCardLeftPixels="10"
                    :helpCardWidth="300"
                    :helpCardMinWidthpx="300"
                    @clickedAway="showPersonalizationContent = false"
                  />
                </transition>
              </v-layout>
            </v-layout>
          </div>
        </F2gCard>
      </v-flex>
    </div>
    <div class="settings__version">
      Copyright © {{ new Date().getFullYear() }}, Flit2go.
      {{ $t("common.rigths") }} v.{{ version }}
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from "vuex";
import { EventBus } from "@/flit2go-web-core/util/event-bus.js";

const isProduction = process.env.VUE_APP_MODE === "production";

export default {
  name: "settings-config",
  data: () => ({
    language: "en",
    results: [5, 10, 20, 50, 100, 200],
    result: "",
    numberResultsCreated: false,
    showDateTooltipCreated: false,
    notificationType: {},
    helpTextSelectedOption: {},
    version: process.env.PACKAGE_VERSION || "0",
    showDateTooltip: false,
    showPersonalizationColorsTopbar: Boolean,
    showPersonalizationColorsContent: Boolean,
    showPersonalizationTopbarHelp: false,
    showPersonalizationContent: false,
    chatPosition: null,
    isProduction,
    showTimeZoneHelp: false,
    showChatPositionHelp: false,
  }),
  computed: {
    ...mapState(["settings", "auth"]),
    ...mapGetters({
      ownerColors: "auth/getOwnerColors",
      userProfile: "auth/getUserProfile",
    }),
    blockedPersonalization() {
      if (this.settings?.settings?.person) {
        return this.settings.settings.person.ownerPerson.blockedSelection;
      }
      return null;
    },
    notificationTypes() {
      return [
        {
          code: this.$CONSTANTS.NOTIFICATION_TYPE.WEB_AND_MAIL,
          name: this.$t("byMailAndWeb"),
        },
        {
          code: this.$CONSTANTS.NOTIFICATION_TYPE.WEB,
          name: this.$t("onlyByWeb"),
        },
        {
          code: this.$CONSTANTS.NOTIFICATION_TYPE.MAIL,
          name: this.$t("onlyByMail"),
        },
        {
          code: this.$CONSTANTS.NOTIFICATION_TYPE.NO_NOTIFICATIONS,
          name: this.$t("withoutNotifications"),
        },
      ];
    },
    helpTextsOptions() {
      return [
        {
          code: this.$CONSTANTS.SETTINGS.HELP.ALWAYS_SHOW,
          name: this.$t("showAlways"),
        },
        {
          code: this.$CONSTANTS.SETTINGS.HELP.ONLY_FORMS,
          name: this.$t("onlyInForms"),
        },
        {
          code: this.$CONSTANTS.SETTINGS.HELP.DO_NOT_SHOW,
          name: this.$t("doNotShow"),
        },
      ];
    },
    chatPositions() {
      return [
        {
          code: this.$CONSTANTS.CHAT_POSITION.RIGHT,
          name: this.$t("Right"),
        },
        {
          code: this.$CONSTANTS.CHAT_POSITION.LEFT,
          name: this.$t("left"),
        },
      ];
    },
    checkHelpTextSettings() {
      let showHelp = false;
      if (
        this.$store.state.auth.userProfile.settings.showDetailHelpMessages &&
        this.$store.state.auth.userProfile.settings.showFormHelpMessages
      ) {
        showHelp = true;
      }
      return showHelp;
    },
  },
  watch: {
    result() {
      if (this.n) {
        this.$store
          .dispatch("settings/updateNumberResults", this.result)
          .then(() => {
            this.$store.commit("auth/SET_NUMBER_RESULTS", this.result);
            localStorage.setItem(
              "user-token",
              this.$store.state.auth.userProfile
            );
            this.$showMessage.success(
              this.$t("errors.modifyCorrectOPl", {
                that: this.$t("common.tablesNumberResults").toLowerCase(),
              })
            );
          })
          .catch(() =>
            this.$showMessage.error(this.$t(this.$CONSTANTS.ERROR.GENERIC))
          );
      } else {
        this.n = true;
      }
    },
    showDateTooltip() {
      if (this.showDateTooltipCreated) {
        this.$store
          .dispatch("settings/updateShowDateTooltips", this.showDateTooltip)
          .then(() => {
            this.$store.commit(
              "settings/SET_SHOW_TOOLTIP",
              this.showDateTooltip
            );
            this.$showMessage.success(
              this.$t("errors.modifyCorrectOPl", {
                that: this.$t("common.data").toLowerCase(),
              })
            );
          })
          .catch(() =>
            this.$showMessage.error(this.$t(this.$CONSTANTS.ERROR.GENERIC))
          );
      } else {
        this.showDateTooltipCreated = true;
      }
    },
  },
  created() {
    this.getSettings();
    this.result = this.$store.getters["auth/getRowsPerPage"];
    this.tooltip = this.auth.userProfile.tooltip;

    this.$store
      .dispatch("settings/getLanguages")
      .then(() => {
        this.language = this.settings.languages.find(
          (language) =>
            language.code ==
            this.$store.state.auth.userProfile.language.toUpperCase()
        ).code;
      })
      .catch(() =>
        this.$showMessage.error(this.$t(this.$CONSTANTS.ERROR.GENERIC))
      );

    this.$store.dispatch("settings/getShowDateTooltips").then(() => {
      this.showDateTooltip = this.settings.showDateTooltip;
      if (!this.settings.showDateTooltip) {
        this.showDateTooltipCreated = true;
      }
    });
    // this.blockedPersonalization =
    //   this.$store.state.auth.userProfile.ownerPerson.blockedSelection;
  },
  methods: {
    changeLanguage() {
      this.$store
        .dispatch("settings/CHANGE_LANG", {
          id: this.$store.state.auth.userProfile.id,
          language: this.language.toLowerCase(),
        })
        .then(() => {
          this.$store.commit("auth/SET_LANGUAGE", this.language.toLowerCase());
          localStorage.setItem(
            "user-language",
            this.$store.state.auth.userProfile.language
          );
          this.setSelectors();
          this.$showMessage.success(
            this.$t("errors.modifyCorrectO", {
              that: this.$t("common.language").toLowerCase(),
            })
          );
        })
        .catch(() =>
          this.$showMessage.error(this.$t(this.$CONSTANTS.ERROR.GENERIC))
        );
    },
    getSettings() {
      this.$store
        .dispatch("settings/getUserSettings")
        .then((resp) => {
          this.showPersonalizationColorsTopbar = resp.colorPersonalization;
          this.showPersonalizationColorsContent =
            resp.showColorPersonalizationContent;
          this.setSelectors();
          this.setHelpTextsVisibility(resp);
        })
        .catch(this.$showMessage.errorGeneric);
    },
    updateHelpTextSettings(attr, value) {
      return new Promise((resolve, reject) => {
        this.updateSettings(attr, value)
          .then(() => {
            this.$store.commit("auth/SET_SETTING_ATTRIBUTE", {
              attr: attr,
              value: value,
            });
            resolve();
          })
          .catch(reject);
      });
    },
    setPersonalizationColorsContent() {
      this.updateSettings(
        "showColorPersonalizationContent",
        this.showPersonalizationColorsContent.toString()
      ).then(() => {
        this.$showMessage.success(this.$t("common.personalizationCorrect"));
        window.location.reload();
      });
    },
    setPersonalizationColorsTopbar() {
      this.updateSettings(
        "colorPersonalization",
        this.showPersonalizationColorsTopbar.toString()
      ).then(() => {
        this.$showMessage.success(this.$t("common.personalizationCorrect"));
        EventBus.$emit(
          "personalizationChanged",
          this.showPersonalizationColorsTopbar
        );
      });
    },
    updateSettings(name, value) {
      return new Promise((resolve, reject) => {
        this.$store
          .dispatch("settings/updateSettings", {
            settingName: name,
            settingValue: value,
          })
          .then(resolve)
          .catch(() => {
            this.$showMessage.errorGeneric();
            reject();
          });
      });
    },
    updateHelpTextsDetailVisibility() {
      let showFormHelpTexts;
      let showDetailHelpTexts;
      if (
        this.helpTextSelectedOption.code ===
        this.$CONSTANTS.SETTINGS.HELP.ALWAYS_SHOW
      ) {
        showFormHelpTexts = true;
        showDetailHelpTexts = true;
      } else if (
        this.helpTextSelectedOption.code ===
        this.$CONSTANTS.SETTINGS.HELP.ONLY_FORMS
      ) {
        showFormHelpTexts = true;
        showDetailHelpTexts = false;
      } else {
        showFormHelpTexts = false;
        showDetailHelpTexts = false;
      }

      const promises = [];
      promises.push(
        this.updateHelpTextSettings(
          "showDetailHelpMessages",
          showDetailHelpTexts
        )
      );
      promises.push(
        this.updateHelpTextSettings("showFormHelpMessages", showFormHelpTexts)
      );
      Promise.all(promises)
        .then(() =>
          this.$showMessage.success(this.$t("common.personalizationCorrect"))
        )
        .catch(this.$showMessage.error);
    },
    setHelpTextsVisibility(resp) {
      if (resp.showDetailHelpMessages && resp.showFormHelpMessages) {
        this.helpTextSelectedOption = {
          code: this.$CONSTANTS.SETTINGS.HELP.ALWAYS_SHOW,
          name: this.$t("showAlways"),
        };
      } else if (!resp.showDetailHelpMessages && resp.showFormHelpMessages) {
        this.helpTextSelectedOption = {
          code: this.$CONSTANTS.SETTINGS.HELP.ONLY_FORMS,
          name: this.$t("onlyInForms"),
        };
      } else {
        this.helpTextSelectedOption = {
          code: this.$CONSTANTS.SETTINGS.HELP.DO_NOT_SHOW,
          name: this.$t("doNotShow"),
        };
      }
    },
    updateNotificationType() {
      const notificationWeb =
        this.notificationType.code ===
          this.$CONSTANTS.NOTIFICATION_TYPE.WEB_AND_MAIL ||
        this.notificationType.code === this.$CONSTANTS.NOTIFICATION_TYPE.WEB;
      const notificationMail =
        this.notificationType.code ===
          this.$CONSTANTS.NOTIFICATION_TYPE.WEB_AND_MAIL ||
        this.notificationType.code === this.$CONSTANTS.NOTIFICATION_TYPE.MAIL;

      this.updateSettings("notificationsWeb", notificationWeb).finally(() =>
        this.updateSettings("notificationsEmail", notificationMail).then(() =>
          this.$showMessage.success(this.$t("common.personalizationCorrect"))
        )
      );
    },
    setSelectors() {
      // set current notification
      let typeNotification = "";
      if (
        this.settings.settings.notificationsWeb &&
        this.settings.settings.notificationsEmail
      ) {
        typeNotification = this.$CONSTANTS.NOTIFICATION_TYPE.WEB_AND_MAIL;
      } else if (this.settings.settings.notificationsWeb) {
        typeNotification = this.$CONSTANTS.NOTIFICATION_TYPE.WEB;
      } else if (this.settings.settings.notificationsEmail) {
        typeNotification = this.$CONSTANTS.NOTIFICATION_TYPE.MAIL;
      } else {
        typeNotification = this.$CONSTANTS.NOTIFICATION_TYPE.NO_NOTIFICATIONS;
      }
      this.notificationType = this.notificationTypes.find(
        (element) => element.code === typeNotification
      );

      // chat position
      if (isProduction) {
        const position = this.settings.settings.chatRightPosition
          ? this.$CONSTANTS.CHAT_POSITION.RIGHT
          : this.$CONSTANTS.CHAT_POSITION.LEFT;
        this.chatPosition = this.chatPositions.find(
          (element) => element.code === position
        );
      }
    },
    updateChatPosition() {
      if (isProduction) {
        const isRightPosition =
          this.$CONSTANTS.CHAT_POSITION.RIGHT === this.chatPosition.code;
        this.updateSettings("chatPosition", isRightPosition).then(() => {
          this.$showMessage.success(this.$t("common.personalizationCorrect"));
          window.location.reload();
        });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.settings {
  &__wrapper {
    padding: 40px 60px;
  }

  &__version {
    margin-left: 60px;
    padding-bottom: 10px;
    color: $gray-1-f2g;
  }

  .help-icon {
    cursor: pointer;
    pointer-events: auto !important;
  }
}
</style>
